import React, { useEffect, useState } from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'
import styled from 'styled-components'
import { Input } from '@workwhile/ui'
import { defs } from 'components/Shared/defs'
import { device } from 'components/Shared/device'
import {
  JET_ASSIGNMENT_DATE_FIELD_LABELS,
  JET_ASSIGNMENT_DATE_FIELD_VALUES,
  JET_ASSIGNMENT_SORT_BY_LABELS,
  JET_ASSIGNMENT_SORT_BY_VALUES,
  JET_ASSIGNMENT_STATUS_OPTIONS,
  JetAssignmentDateField,
  JetAssignmentSortBy,
  JetAssignmentSortDir,
  JetAssignmentStatus,
} from 'pages/Jet/shared/constants/assignmentStatus'
import { useJetAssignmentsFiltersQueryState } from '../hooks/useJetAssignmentsFiltersQueryState'

type JetAssignmentsFilters = ReturnType<
  typeof useJetAssignmentsFiltersQueryState
>[0]

type StatusOption = { label: string; value: JetAssignmentStatus }
type DateFieldOption = { label: string; value: JetAssignmentDateField }
type NumberOption = { label: string; value: number }
type SortByOption = { label: string; value: JetAssignmentSortBy }
type SortDirOption = { label: string; value: JetAssignmentSortDir }

const DAYS_AGO_OPTIONS: NumberOption[] = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
]

const DATE_FIELD_OPTIONS: DateFieldOption[] =
  JET_ASSIGNMENT_DATE_FIELD_VALUES.map((value) => ({
    value,
    label: JET_ASSIGNMENT_DATE_FIELD_LABELS[value],
  }))

const SORT_BY_OPTIONS: SortByOption[] = JET_ASSIGNMENT_SORT_BY_VALUES.map(
  (value) => ({ value, label: JET_ASSIGNMENT_SORT_BY_LABELS[value] })
)

const SORT_DIR_OPTIONS: SortDirOption[] = [
  { label: 'Newest first', value: 'desc' },
  { label: 'Oldest first', value: 'asc' },
]

const SEARCH_DEBOUNCE_MS = 500

const findSelectedStatuses = (values: JetAssignmentStatus[] | null) =>
  values
    ? JET_ASSIGNMENT_STATUS_OPTIONS.filter((o) => values.includes(o.value))
    : []

const toStatusValues = (selected: MultiValue<StatusOption>) =>
  selected.length ? selected.map((o) => o.value) : null

type JetAssignmentsFilterBarProps = {
  filters: JetAssignmentsFilters
  setFilters: (update: Partial<JetAssignmentsFilters>) => void
}

export const JetAssignmentsFilterBar = ({
  filters,
  setFilters,
}: JetAssignmentsFilterBarProps) => {
  const [assignmentIdInput, setAssignmentIdInput] = useState(
    filters.assignmentId
  )
  const [shiftIdInput, setShiftIdInput] = useState(filters.shiftId)

  // Keep in sync with URL-driven changes (e.g. back/forward navigation).
  useEffect(() => {
    setAssignmentIdInput(filters.assignmentId)
  }, [filters.assignmentId])

  useEffect(() => {
    setShiftIdInput(filters.shiftId)
  }, [filters.shiftId])

  // Debounce so each keystroke doesn't refetch (and reset paging) immediately.
  useEffect(() => {
    if (assignmentIdInput === filters.assignmentId) {
      return
    }
    const timeout = setTimeout(() => {
      setFilters({ assignmentId: assignmentIdInput })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [assignmentIdInput, filters.assignmentId, setFilters])

  useEffect(() => {
    if (shiftIdInput === filters.shiftId) {
      return
    }
    const timeout = setTimeout(() => {
      setFilters({ shiftId: shiftIdInput })
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timeout)
  }, [shiftIdInput, filters.shiftId, setFilters])

  const selectedDateField =
    DATE_FIELD_OPTIONS.find((o) => o.value === filters.dateField) ?? null
  const selectedDaysAgo =
    DAYS_AGO_OPTIONS.find((o) => o.value === filters.daysAgo) ?? null
  const selectedSortBy =
    SORT_BY_OPTIONS.find((o) => o.value === filters.sortBy) ?? null
  const selectedSortDir =
    SORT_DIR_OPTIONS.find((o) => o.value === filters.sortDir) ?? null

  return (
    <FilterBarWrapper showBorderTop showBorderBottom>
      <SelectGroup>
        <Input
          type="text"
          placeholder="Assignment ID"
          value={assignmentIdInput}
          onChange={(e) => setAssignmentIdInput(e.target.value)}
          style={{
            minWidth: 200,
            marginRight: defs.marginM,
            marginTop: defs.marginXS,
            marginBottom: defs.marginXS,
          }}
        />
        <Input
          type="text"
          inputMode="numeric"
          placeholder="Shift ID"
          value={shiftIdInput}
          onChange={(e) =>
            setShiftIdInput(e.target.value.replace(/[^0-9]/g, ''))
          }
          style={{
            minWidth: 160,
            marginRight: defs.marginM,
            marginTop: defs.marginXS,
            marginBottom: defs.marginXS,
          }}
        />
        <Select
          isMulti
          isClearable
          isSearchable={false}
          options={JET_ASSIGNMENT_STATUS_OPTIONS}
          styles={wideStyles}
          name="statuses"
          placeholder="Status"
          value={findSelectedStatuses(filters.statuses)}
          onChange={(selected: MultiValue<StatusOption>) =>
            setFilters({ statuses: toStatusValues(selected) })
          }
          closeMenuOnSelect={false}
        />
        <Select
          isSearchable={false}
          options={DATE_FIELD_OPTIONS}
          styles={customStyles}
          name="dateField"
          placeholder="Date field"
          value={selectedDateField}
          onChange={(selected: SingleValue<DateFieldOption>) => {
            if (selected) {
              setFilters({ dateField: selected.value })
            }
          }}
        />
        <Select
          isClearable
          isSearchable={false}
          options={DAYS_AGO_OPTIONS}
          styles={customStyles}
          name="daysAgo"
          placeholder="Time range"
          value={selectedDaysAgo}
          onChange={(selected: SingleValue<NumberOption>) =>
            setFilters({ daysAgo: selected ? selected.value : null })
          }
        />
        <Select
          isSearchable={false}
          options={SORT_BY_OPTIONS}
          styles={customStyles}
          name="sortBy"
          placeholder="Sort by"
          value={selectedSortBy}
          onChange={(selected: SingleValue<SortByOption>) => {
            if (selected) {
              setFilters({ sortBy: selected.value })
            }
          }}
        />
        <Select
          isSearchable={false}
          options={SORT_DIR_OPTIONS}
          styles={customStyles}
          name="sortDir"
          placeholder="Order"
          value={selectedSortDir}
          onChange={(selected: SingleValue<SortDirOption>) => {
            if (selected) {
              setFilters({ sortDir: selected.value })
            }
          }}
        />
      </SelectGroup>
    </FilterBarWrapper>
  )
}

const customStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 180,
    marginRight: defs.marginM,
    marginTop: defs.marginXS,
    marginBottom: defs.marginXS,
    fontSize: defs.fontS,
  }),
}

const wideStyles = {
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    minWidth: 260,
    marginRight: defs.marginM,
    marginTop: defs.marginXS,
    marginBottom: defs.marginXS,
    fontSize: defs.fontS,
  }),
}

const FilterBarWrapper = styled.div<{
  showBorderTop?: boolean
  showBorderBottom?: boolean
}>`
  border-bottom: ${(props) => (props.showBorderBottom ? '1px solid' : null)};
  border-top: ${(props) => (props.showBorderTop ? '1px solid' : null)};
  border-color: ${defs.veryLightBlack};
  padding: ${defs.paddingS};
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: #fafafa;
  @media ${device.smallerThanMobileL} {
    font-size: ${defs.fontS};
    height: 100%;
    padding-left: ${defs.paddingM};
  }
`

const SelectGroup = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
`
